import React, {ReactNode} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {
  mediaMobileOnly,
  mediaTabletAndUp,
  tabletAndUp,
} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useExpensiveMedia} from '../../../hooks/useExpensiveMedia';

interface ProductCardBaseProps {
  imageBlock: ReactNode;
  descriptionBlock: ReactNode;
  priceBlock: ReactNode;
  quantityBlock: ReactNode;
  deleteBlock: ReactNode;
  className?: string;
}

const quantityChangeLeftColumnByViewport = {
  [mediaMobileOnly]: true,
  [mediaTabletAndUp]: false,
};

export const ProductCardBase = ({
  imageBlock,
  descriptionBlock,
  priceBlock,
  quantityBlock,
  deleteBlock,
  className,
}: ProductCardBaseProps) => {
  const quantityChangeLeftColumn = useExpensiveMedia(
    quantityChangeLeftColumnByViewport,
    true,
  );

  return (
    <Container className={className} data-testid="productCardContainer">
      <LeftColumn>
        <ImageBlock>{imageBlock}</ImageBlock>
        {quantityChangeLeftColumn && quantityBlock && (
          <QuantityBlock>{quantityBlock}</QuantityBlock>
        )}
      </LeftColumn>

      <MiddleColumn>
        <DescriptionBlock data-testid="productDetailsSection">
          {descriptionBlock}
        </DescriptionBlock>
        {!quantityChangeLeftColumn && (
          <QuantityBlock>{quantityBlock}</QuantityBlock>
        )}
      </MiddleColumn>
      <RightColumn>
        <DeleteBlock>{deleteBlock}</DeleteBlock>
        <PriceBlock>{priceBlock}</PriceBlock>
      </RightColumn>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  position: relative;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const MiddleColumn = styled.div<{$maxWidth?: boolean}>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  padding-top: 0.75rem;
  margin-left: 1.25rem;

  ${({$maxWidth}) =>
    $maxWidth &&
    css`
      max-width: 140px;
    `};

  ${tabletAndUp(
    css`
      margin-left: 1.5rem;
    `,
  )}
`;

const RightColumn = styled.div`
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  align-items: flex-end;
  align-self: stretch;
  position: relative;

  ${tabletAndUp(
    css`
      flex: 1;
    `,
  )}
`;

const ImageBlock = styled.div`
  position: relative;
  width: 120px;
  flex-shrink: 0;
  display: flex;
`;

const DescriptionBlock = styled.div`
  font-size: ${FONT_SIZES.px12};
  line-height: 1.33;
  letter-spacing: normal;
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Black};
`;

const PriceBlock = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const QuantityBlock = styled.div`
  margin-top: 5px;
  ${tabletAndUp(css`
    margin-top: 20px;
  `)}
  flex: 1;
`;

const DeleteBlock = styled.div``;
